<template>
  <UiButton
    unset-all
    :class="
      cn(
        'video-card relative flex flex-col overflow-hidden rounded-lg p-8 text-start md:p-5',
        { group: !isCurrentlyPlaying },
        props.classes?.root,
        props.class
      )
    "
    @click="() => (currentPlaying = index)"
  >
    <NuxtImg
      class="absolute inset-0 z-0 h-full w-full object-cover transition-transform ease-out group-hover:scale-110"
      :src="getImageUrl(video.thumbnail)"
      :alt="video.title"
      loading="lazy"
    />

    <div class="absolute inset-0 z-0 bg-gradient-to-t from-black/90 to-black/5" />

    <UiButton
      class="z-1 absolute left-1/2 top-1/2 grid -translate-x-1/2 -translate-y-1/2 place-content-center rounded-full border-2 border-white p-3 transition-opacity group-hover:opacity-80"
      unset-all
      is="div"
    >
      <PlayIcon class="translate-x-px text-white" :size="28" />
    </UiButton>

    <div class="z-1 mt-auto flex flex-col gap-3 pe-10 text-white transition-opacity group-hover:opacity-80">
      <span :class="cn('text-2xl font-semibold', props.classes?.title)">{{ video.title }}</span>
      <p v-if="video.description" :class="cn('text-normal text-sm', props.classes?.description)">
        {{ video.description }}
      </p>

      <span v-if="video?.hint" :class="cn('text-xs font-light', props.classes?.hint)">{{ video.hint }}</span>
    </div>
  </UiButton>

  <UiDialogBasic
    v-if="isMounted"
    :open="isCurrentlyPlaying"
    @update:open="(isOpen) => !isOpen && resetCurrentPlayingVideo()"
    :classes="{
      panel:
        'home-video-card-modal aspect-video overflow-hidden border-0 bg-black p-0 md:h-full md:max-w-none md:rounded-none md:bg-transparent',
      overlay: 'backdrop-blur-sm',
      close: 'text-white hover:text-white/80',
    }"
  >
    <UiDialogTitle class="sr-only">{{ video.title }}</UiDialogTitle>

    <div
      :class="
        cn(
          'z-1 group relative grid h-full w-full place-content-center transition-transform duration-700 md:bg-black/10 md:px-4',
          isCurrentlyPlaying ? 'pointer-events-auto translate-y-0' : 'translate-y-full'
        )
      "
    >
      <OnClickOutside class="relative aspect-video w-full" @trigger="onTriggerOutside">
        <video
          class="block aspect-video w-full md:max-w-2xl md:rounded-lg"
          v-if="video.video"
          v-auto-animate
          @click="togglePlay"
          ref="videoRef"
          playsinline
        />

        <LandingHomeOldSectionVideosControls
          v-model:model-value="currentTime"
          v-model:hide="hide"
          @toggle-play="togglePlay"
          :buffered
          :seeking
          :duration
          :playing
          :waiting
        />
      </OnClickOutside>
    </div>
  </UiDialogBasic>
</template>

<script setup lang="ts">
import { OnClickOutside } from "@vueuse/components"
import { PlayIcon } from "lucide-vue-next"

import { HomepageVideo } from "./Videos.vue"

export interface VideoCardClasses {
  root: ClassValue
  title: ClassValue
  description: ClassValue
  hint: ClassValue
}

export interface VideoCardProps {
  class?: ClassValue
  index?: number | null
  video: HomepageVideo
  isPlaying?: boolean
  classes?: Partial<VideoCardClasses>
}

const props = defineProps<VideoCardProps>()

const emit = defineEmits<{
  (event: "toggle", value: { isPlaying: boolean }): void
}>()

const currentPlaying = defineModel<Nullable<number>>("playing", { default: null })
const isCurrentlyPlaying = computed(() => currentPlaying.value === props.index)

const videoRef = ref<HTMLVideoElement | null>(null)
const hide = ref(false)
const { isMobile } = useDisplay()
const isMounted = useMounted()

const { playing, buffered, currentTime, duration, waiting, seeking } = useMediaControls(videoRef, {
  src: {
    src: getImageUrl(props.video.video),
    type: "video/mp4",
  },
})

const togglePlay = () => {
  console.log("togglePlay", waiting.value)
  if (waiting.value) return

  playing.value = !playing.value

  // Handles the visibility state of the controls only on mobile.
  if (isMobile.value) hide.value = false
}

const onTriggerOutside = () => {
  // Handles the visibility state of the controls only on mobile.

  if (!playing.value || !isMobile.value) return
  hide.value = true
}

const resetCurrentPlayingVideo = () => {
  if (isCurrentlyPlaying.value) currentPlaying.value = null
}

watchEffect(() => {
  if (isCurrentlyPlaying.value && videoRef.value) {
    videoRef.value.currentTime = 0
    videoRef.value?.play()
  }
})
</script>

<style lang="scss">
@screen min-md {
  .home-video-card-modal {
    max-width: calc(35dvw + 35dvh);
  }
}
</style>
